// orderSlice.js
import { createSlice } from "@reduxjs/toolkit";

const loadLocal = () => {
  try {
    return JSON.parse(localStorage.getItem("cartOrders")) || {};
  } catch {
    return {};
  }
};

const persistUnplaced = (orderData) => {
  const unplaced = {};
  for (const [key, order] of Object.entries(orderData)) {
    const onlyUnplaced = (order?.items || []).filter((it) => !it.id);
    if (onlyUnplaced.length) {
      unplaced[key] = {
        tableId: order?.tableId ?? (key !== "temp" ? key : null),
        remarks: order?.remarks || "",
        items: onlyUnplaced,
      };
    }
  }
  localStorage.setItem("cartOrders", JSON.stringify(unplaced));
};

const initialState = {
  orderData: loadLocal(), // only unplaced persisted; runtime will hold placed+unplaced
  orderId: null,
};

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    // Generic merge for any key (including "temp").
    setOrderData: (state, action) => {
      for (const [key, incoming] of Object.entries(action.payload)) {
        const existing = state.orderData[key] || { items: [] };
        state.orderData[key] = {
          ...existing,
          ...incoming,
          items: Array.isArray(incoming.items) ? incoming.items : existing.items,
        };
      }
      persistUnplaced(state.orderData);
    },

    // Set API order for a table (usually placedItems + any chosen unplaced).
    setApiOrderData: (state, action) => {
      for (const [tableId, incoming] of Object.entries(action.payload)) {
        const existing = state.orderData[tableId] || { items: [] };
        state.orderData[tableId] = {
          ...existing,
          ...incoming,
          items: Array.isArray(incoming.items) ? incoming.items : existing.items,
        };
      }
      persistUnplaced(state.orderData);
    },

    // Move all temp items into a given table (append).
    transferTempToTable: (state, action) => {
      const tableId = action.payload;
      const temp = state.orderData["temp"];
      if (!temp?.items?.length) return;

      const table = state.orderData[tableId] || { tableId, remarks: "", items: [] };
      state.orderData[tableId] = {
        ...table,
        items: [...table.items, ...temp.items],
      };

      delete state.orderData["temp"];
      persistUnplaced(state.orderData);
    },

    addOrderItem: (state, action) => {
      const { tableId, newItem } = action.payload;
      const bucket = state.orderData[tableId] || { tableId, remarks: "", items: [] };
      bucket.items = [...(bucket.items || []), newItem];
      state.orderData[tableId] = bucket;
      persistUnplaced(state.orderData);
    },

    setOrderId: (state, action) => {
      state.orderId = action.payload;
    },

    resetOrder: (state) => {
      state.orderData = {};
      state.orderId = null;
      localStorage.removeItem("cartOrders");
    },

    resetTableOrder: (state, action) => {
      const tableId = action.payload;
      delete state.orderData[tableId];
      persistUnplaced(state.orderData);
    },

    deleteOrderItems: (state, action) => {
      const { tableId, itemIndexes } = action.payload;
      const bucket = state.orderData[tableId];
      if (!bucket) return;

      const sorted = [...itemIndexes].sort((a, b) => b.itemIndex - a.itemIndex);
      sorted.forEach(({ itemIndex }) => {
        bucket.items.splice(itemIndex, 1);
      });
      persistUnplaced(state.orderData);
    },

    deleteUnplacedOrderItems: (state, action) => {
      const { tableId, itemIndexes } = action.payload;
      const bucket = state.orderData[tableId];
      if (!bucket) return;

      const sorted = [...itemIndexes].sort((a, b) => b.itemIndex - a.itemIndex);
      sorted.forEach(({ itemIndex }) => {
        const it = bucket.items[itemIndex];
        if (it && !it.id) bucket.items.splice(itemIndex, 1);
      });

      if (!bucket.items.length) delete state.orderData[tableId];
      persistUnplaced(state.orderData);
    },
  },
});

export const {
  setOrderData,
  setApiOrderData,
  addOrderItem,
  setOrderId,
  resetOrder,
  resetTableOrder,
  deleteOrderItems,
  deleteUnplacedOrderItems,
  transferTempToTable,
} = orderSlice.actions;

export default orderSlice.reducer;
